import React, { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom"; 
import "../App.css";

function QuestionNumbers({ onQuestionClick }) {
  const location = useLocation(); 
  const { assessmentId } = location.state || {};

  const scrollContainerRef = useRef(null);
  const [questions, setQuestions] = useState([]);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (!assessmentId) {
      console.error("Assessment ID is missing.");
      return;
    }

    const fetchQuestionCount = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API}/getQuestionCount?assessment_id=${assessmentId}`
        );
        const data = await response.json();

        if (data.success) {
          const questionNumbers = Array.from(
            { length: data.count },
            (_, index) => index + 1
          );
          setQuestions(questionNumbers);
          setSelectedQuestion(questionNumbers[0]);
          onQuestionClick(0); 
        } else {
          console.error("Failed to fetch question count:", data.message);
        }
      } catch (error) {
        console.error("Error fetching question count:", error);
      }
    };

    fetchQuestionCount();
  }, [assessmentId, onQuestionClick]);

  useEffect(() => {
    if (scrollContainerRef.current) {
      const selectedElement = scrollContainerRef.current.children[currentIndex];
      if (selectedElement) {
        selectedElement.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
      }
    }
  }, [currentIndex]);

  const scroll = (direction, step = 1) => {
    const scrollAmount = 100 * step;

    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: direction === "next" ? scrollAmount : -scrollAmount,
        behavior: "smooth",
      });
    }

    if (direction === "next" && currentIndex < questions.length - 1) {
      const nextIndex = Math.min(currentIndex + step, questions.length - 1);
      setCurrentIndex(nextIndex);
      setSelectedQuestion(questions[nextIndex]);
      onQuestionClick(nextIndex);
    } else if (direction === "prev" && currentIndex > 0) {
      const prevIndex = Math.max(currentIndex - step, 0);
      setCurrentIndex(prevIndex);
      setSelectedQuestion(questions[prevIndex]);
      onQuestionClick(prevIndex);
    }
  };

  const handleQuestionClick = (question, index) => {
    setSelectedQuestion(question);
    setCurrentIndex(index);
    onQuestionClick(index);
  };

  const buttonStyles = {
    width: "200px",
    backgroundColor: "#263F86",
    color: "#ffffff",
    fontFamily: "Manrope-Regular",
  };

  if (!assessmentId) {
    return <p className="text-danger">Assessment ID is required.</p>;
  }

  return (
    <>
      <div className="d-flex align-items-center">
        <button
          onClick={() => scroll("prev", 10)}
          className="btn btn-outline-secondary mx-2"
          aria-label="Scroll Left"
        >
          &#8592;
        </button>

        <div
          ref={scrollContainerRef}
          className="question-numbers-container d-flex overflow-auto"
        >
          {questions.map((question, index) => (
            <div
              key={index}
              className={`question-number-box border rounded px-3 py-2 mx-1 ${
                selectedQuestion === question ? "selected" : ""
              }`}
              onClick={() => handleQuestionClick(question, index)}
            >
              {question}
            </div>
          ))}
        </div>

        <button
          onClick={() => scroll("next", 10)}
          className="btn btn-outline-secondary mx-2"
          aria-label="Scroll Right"
        >
          &#8594;
        </button>
      </div>
      <footer className="custom-container bg-light text-white py-3 mt-auto position-fixed w-100 bottom-0">
        <div className="d-flex justify-content-center align-items-center">
          <button
            onClick={() => scroll("prev")}
            type="button"
            className="btn btn-primary mx-2"
            style={buttonStyles}
          >
            &#8592; Previous
          </button>
          <button
            onClick={() => scroll("next")}
            type="button"
            className="btn btn-primary mx-2"
            style={buttonStyles}
          >
            Next &#8594;
          </button>
        </div>
      </footer>
    </>
  );
}

export default QuestionNumbers;
